/* eslint-disable folders/match-regex, filenames/match-regex */
import { setupI18n } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";

export const LANGUAGE_CODE = {
  English: "en",
  Spanish: "es",
  Portuguese: "pt",
  French: "fr",
  German: "de",
  Italian: "it",
  Russian: "ru",
  Chinese: "zh-hans",
  Korean: "ko",
  Japanese: "ja",
  Vietnamese: "vi",
  Thai: "th",
  Arabic: "ar",
  Polish: "pl",
  Slovak: "sk",
  Czech: "cs",
  Ukrainian: "uk",
  Hindi: "hi",
  Romanian: "ro"
};

export const LOCALES = Object.values(LANGUAGE_CODE);

export const i18nCore = setupI18n({
  locale: "en",
  locales: LOCALES,
  messages: {}
});

let currentLocaleCache = {};
let isTranslationEmptyResult = true;
let generatedHashKeysCache = {};

/**
 * this function checks translationMessages for a locale single time
 * optimistically by caching the result for subsequent check
 */
const isTranslationEmpty = (translationMessages) => {
  const currentLocale = i18nCore._locale;
  if (currentLocaleCache !== currentLocale) {
    currentLocaleCache = currentLocale;
    isTranslationEmptyResult = true;
    for (const key in translationMessages) {
      if (key) {
        isTranslationEmptyResult = false;
        break;
      }
    }
  }
  return isTranslationEmptyResult;
};

/**
 * this function returns hashKey for the given key if available in cache,
 * if not, it generates hashKey and stores in cache before returning.
 *
 */
const getHashKey = (key) => {
  if (!generatedHashKeysCache[key]) {
    generatedHashKeysCache[key] = generateMessageId(key);
  }
  return generatedHashKeysCache[key];
};

/**
 * As extracted translation files have hashId as key, service messages & system fields from api response can't be
 * translated when the actual message sent to i18n._ function. So here we intercept the actual i18n._ function
 * and trying to translate the hashKey if hashKey has the translation.
 * eg: `i18n._(error.message, error.args)` where error is api failure response.
 */

export const i18n = {
  _: (id, values, options) => {
    let key = id;
    if (!key) {
      key = "";
    }
    if (typeof key !== "string") {
      key = key.id;
    }
    let translationMessages = getTranslationMessages();
    if (
      key &&
      !isTranslationEmpty(translationMessages) &&
      !translationMessages[key]
    ) {
      let hashKey = getHashKey(key);
      if (translationMessages[hashKey]) {
        return i18nCore._(hashKey, values, options);
      }
    }
    return i18nCore._(id, values, options);
  }
};

/**
 * Due to stroybook's current architecture, triggered service's lingui config (with runtimeConfigModule)
 * will be applied to all services instead of specific service's own lingui config (eg: base has specific
 * runtimeConfigModule). So wherever @lingui/macro and i18n import used together will get import error
 * while compling in storybook. To rectify this issue this customI18n will be mapped for stroybook as
 * runtimeConfigModule. So stroybook will use customI18n in import without clashing with existing i18n import.
 */

export const customI18n = i18n;

/**
 * i18nLoadAndActivate - load & activate translation messages for the selected locale
 * @param {string} locale - language code
 * @param {messages} messages - translated messages json
 * @param {boolean} resetBeforeLoad - to reset before loading new messages
 * @param {function} onDone - callback after load and activate done
 */
export async function i18nLoadAndActivate({
  locale,
  messages = {},
  resetBeforeLoad = false,
  onDone = () => {}
}) {
  if (resetBeforeLoad && i18nCore?._messages) {
    i18nCore._messages = {};
  }
  const missingMessages = window.remoteMissingLanguage?.default?.messages || {};

  i18nCore.loadAndActivate({
    locale,
    messages: Object.assign({}, missingMessages, messages)
  });
  onDone();
}

export function loadLocale(localeSrc) {
  return new Promise((resolve, reject) => {
    const moduleBlob = new Blob(
      [
        `import * as m from '${localeSrc}';`,
        `window['${localeSrc}'] = {default:m}`
      ],
      {
        type: "text/javascript"
      }
    );

    let script = document.getElementById("LOCALES");
    if (script) {
      document.body.removeChild(script);
    }
    script = document.createElement("script");
    script.id = "LOCALES";
    script.type = "module";
    script.src = URL.createObjectURL(moduleBlob);
    script.async = true;
    script.onload = () => {
      console.info("succes calling ", window[localeSrc]);
      resolve(window[localeSrc]);
    };
    script.onerror = (err) => {
      console.info("on error is calling ", err);
      reject();
    };
    document.body.appendChild(script);
  });
}

export function loadLanguage({
  locale,
  kind,
  onDone = () => {},
  onError = () => {}
}) {
  // eslint-disable-next-line max-len
  let baseUrl = `${window.location.protocol}${window.publicPath}static/translation/${kind}/${window.translationBuildNumber}`;
  if (!window.remoteMissingLanguage) {
    loadLocale(`${baseUrl}/en/missing.mjs`)
      .then((missing) => {
        window.remoteMissingLanguage = missing || { default: {} };
        i18nLoadAndActivate({
          locale,
          messages: window.remoteLanguage?.default?.messages || {},
          onDone
        });
        console.warn("Missing en messages loaded");
      })
      .catch((err) => {
        onError(err);
        console.error("Failed to load missing en messages", err);
      });
  }

  loadLocale(`${baseUrl}/${locale}/messages.mjs`)
    .then((language) => {
      window.remoteLanguage = language || { default: {} };
      i18nLoadAndActivate({
        locale,
        messages: window.remoteLanguage.default.messages,
        onDone
      });
      console.warn("Translation loaded for", locale);
    })
    .catch((err) => {
      onError(err);
      console.error("Failed to load", locale, "translation", err);
    });
}

export function getTranslationMessages() {
  if (i18nCore && i18nCore._messages?.[i18nCore._locale]) {
    return i18nCore._messages[i18nCore._locale];
  }
  return {};
}
